// services/workflowEngine.js
import { getWorkflow, getTask } from '../config/workflows.js';

/**
 * Does a task apply to a project, given that project's custom-field values?
 *
 * A task with no `appliesWhen` rule always applies. A rule names a field_key and either an `in`
 * allowlist or a `notIn` blocklist of values. When the field has no value the task is treated as
 * APPLICABLE for `notIn` rules — the conservative choice, since hiding a task the user still owes
 * is worse than showing one they don't. (`in` rules require an explicit match.)
 */
const taskApplies = (task, fieldValues) => {
  const rule = task.appliesWhen;
  if (!rule) return true;
  const value = fieldValues ? fieldValues[rule.field] : undefined;
  if (rule.in) return rule.in.includes(value);
  if (rule.notIn) return !rule.notIn.includes(value);
  return true;
};

export class WorkflowEngine {

  /**
   * The tasks that apply to a project. Every other method filters through this, so a Not Applicable
   * task is excluded from dependency checks, the "all complete" test, and the status label.
   * @param {string} projectType - The project type
   * @param {Object} fieldValues - Map of field_key -> value for this project (optional)
   * @returns {Array} - Array of applicable task objects
   */
  static getApplicableTasks(projectType, fieldValues) {
    const workflow = getWorkflow(projectType);
    if (!workflow) return [];
    return workflow.tasks.filter(task => taskApplies(task, fieldValues));
  }

  /**
   * Whether a single task applies to a project. Used by the UI to render N/A rows.
   * @param {string} projectType - The project type
   * @param {number} taskId - The task ID
   * @param {Object} fieldValues - Map of field_key -> value for this project (optional)
   * @returns {boolean}
   */
  static taskApplies(projectType, taskId, fieldValues) {
    const task = getTask(projectType, taskId);
    if (!task) return false;
    return taskApplies(task, fieldValues);
  }


  /**
   * Check if a task can be completed based on its dependencies
   * @param {string} projectType - The project type (e.g., 'Characterization')
   * @param {number} taskId - The task ID to check
   * @param {Array} completedTaskIds - Array of completed task IDs
   * @returns {boolean} - True if task can be completed
   */
  static canCompleteTask(projectType, taskId, completedTaskIds, fieldValues) {
    const task = getTask(projectType, taskId);
    if (!task) return false;
    if (!taskApplies(task, fieldValues)) return false;

    // Dependencies that don't apply to this project are dropped rather than blocking forever.
    const applicableIds = this.getApplicableTasks(projectType, fieldValues).map(t => t.id);
    return task.dependencies
      .filter(depId => applicableIds.includes(depId))
      .every(depId => completedTaskIds.includes(depId));
  }

  /**
   * Check if a task can be uncompleted (no later tasks depend on it being complete)
   * @param {string} projectType - The project type
   * @param {number} taskId - The task ID to check
   * @param {Array} completedTaskIds - Array of completed task IDs
   * @returns {boolean} - True if task can be uncompleted
   */
  static canUncompleteTask(projectType, taskId, completedTaskIds, fieldValues) {
    const workflow = getWorkflow(projectType);
    if (!workflow) return false;

    // Find all applicable tasks that depend on this task (N/A tasks can't block an uncomplete)
    const dependentTasks = this.getApplicableTasks(projectType, fieldValues).filter(task =>
      task.dependencies.includes(taskId)
    );

    // Check if any dependent tasks are currently completed
    return !dependentTasks.some(task => completedTaskIds.includes(task.id));
  }

  /**
   * Get all tasks that are currently available to be completed
   * @param {string} projectType - The project type
   * @param {Array} completedTaskIds - Array of completed task IDs
   * @returns {Array} - Array of available task objects
   */
  static getAvailableTasks(projectType, completedTaskIds, fieldValues) {
    return this.getApplicableTasks(projectType, fieldValues).filter(task =>
      !completedTaskIds.includes(task.id) &&
      this.canCompleteTask(projectType, task.id, completedTaskIds, fieldValues)
    );
  }

  /**
   * Get the next logical tasks that should be completed
   * @param {string} projectType - The project type
   * @param {Array} completedTaskIds - Array of completed task IDs
   * @returns {Array} - Array of next task objects
   */
  static getNextTasks(projectType, completedTaskIds, fieldValues) {
    const availableTasks = this.getAvailableTasks(projectType, completedTaskIds, fieldValues);

    // Return tasks with the lowest ID numbers (earliest in sequence)
    return availableTasks.sort((a, b) => a.id - b.id);
  }

  /**
   * Get project status based on completion
   * @param {string} projectType - The project type
   * @param {Array} completedTaskIds - Array of completed task IDs
   * @returns {string} - Status string matching your existing format
   */
  static getProjectStatus(projectType, completedTaskIds, fieldValues) {
    const workflow = getWorkflow(projectType);
    if (!workflow) return 'Unknown';

    if (completedTaskIds.length === 0) {
      return '0 - Not Started';
    }

    // "Complete" means every APPLICABLE task is done. Without this, a project whose workflow skips
    // tasks (an Advance data sheet, a Characterization with no Level 2/3 testing) could never
    // reach Complete because the skipped tasks stay permanently unchecked.
    const applicableTasks = this.getApplicableTasks(projectType, fieldValues);

    // Find the highest completed task
    const highestCompletedId = Math.max(...completedTaskIds.filter(id =>
      applicableTasks.some(task => task.id === id)
    ));

    if (highestCompletedId === -Infinity) {
      return '0 - Not Started';
    }

    // Get the task name for the highest completed task
    const highestTask = applicableTasks.find(task => task.id === highestCompletedId);

    const allTasksComplete = applicableTasks.every(task =>
      completedTaskIds.includes(task.id)
    );

    if (allTasksComplete) {
      const lastTask = applicableTasks[applicableTasks.length - 1];
      return `${lastTask.id} - Complete`;
    }

    // Return status with task name
    return `${highestCompletedId} - ${highestTask.name}`;
  }

  /**
   * Validate a task state change
   * @param {string} projectType - The project type
   * @param {number} taskId - The task ID
   * @param {boolean} newState - True for complete, false for incomplete
   * @param {Array} currentCompletedIds - Current completed task IDs
   * @returns {Object} - {valid: boolean, error?: string}
   */
  static validateTaskChange(projectType, taskId, newState, currentCompletedIds, fieldValues) {
    const task = getTask(projectType, taskId);
    if (!task) {
      return { valid: false, error: 'Task not found' };
    }

    if (!taskApplies(task, fieldValues)) {
      return {
        valid: false,
        error: 'This step does not apply to this project and cannot be checked.'
      };
    }

    if (newState) {
      // Trying to complete task
      if (!this.canCompleteTask(projectType, taskId, currentCompletedIds, fieldValues)) {
        const applicableIds = this.getApplicableTasks(projectType, fieldValues).map(t => t.id);
        const missingDeps = task.dependencies.filter(depId =>
          applicableIds.includes(depId) && !currentCompletedIds.includes(depId)
        );
        return {
          valid: false,
          error: `Cannot complete task. Missing dependencies: ${missingDeps.join(', ')}`
        };
      }
    } else {
      // Trying to uncomplete task
      if (!this.canUncompleteTask(projectType, taskId, currentCompletedIds, fieldValues)) {
        return {
          valid: false,
          error: 'Cannot uncomplete task. Later tasks depend on this one.'
        };
      }
    }

    return { valid: true };
  }

  /**
   * Get tasks grouped by their group name for UI rendering
   * @param {string} projectType - The project type
   * @param {Array} completedTaskIds - Array of completed task IDs
   * @param {Object} taskStates - Object mapping task IDs to their UI state
   * @returns {Object} - Tasks grouped by group name
   */
  static getTasksByGroup(projectType, completedTaskIds, taskStates, fieldValues) {
    const workflow = getWorkflow(projectType);
    if (!workflow) return {};

    const groupedTasks = {};

    workflow.groups.forEach(group => {
      groupedTasks[group.name] = {
        groupConfig: group,
        // N/A tasks are kept in the list (with applies: false) so the UI can render them greyed out
        // rather than silently shortening the workflow.
        tasks: workflow.tasks
          .filter(task => task.group === group.name)
          .map(task => ({
            ...task,
            state: taskStates[task.id] || {
              checked: completedTaskIds.includes(task.id),
              date: '',
              user: '',
              originalState: completedTaskIds.includes(task.id)
            },
            applies: taskApplies(task, fieldValues),
            canComplete: this.canCompleteTask(projectType, task.id, completedTaskIds, fieldValues),
            canUncomplete: this.canUncompleteTask(projectType, task.id, completedTaskIds, fieldValues)
          }))
      };
    });

    return groupedTasks;
  }

  /**
   * Get workflow groups configuration for layout
   * @param {string} projectType - The project type
   * @returns {Array} - Array of group configuration objects
   */
  static getWorkflowGroups(projectType) {
    const workflow = getWorkflow(projectType);
    return workflow?.groups || [];
  }
}